import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2, UserCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import { FloatingLanguageSelector } from '../components/LanguageSelector';
import LegalAcceptance from '../components/LegalAcceptance';
import { redirectToGoogleLogin } from '../lib/googleAuth';
import { redirectToAppleLogin } from '../lib/appleAuth';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { useNativeSocialLogin } from '../hooks/useNativeSocialLogin';
import { useSoltarAlRegresar } from '../hooks/useSoltarAlRegresar';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useConnectingRetry } from '../hooks/useConnectingRetry';
import { AUTH_FAILURE, classifyAuthFailure } from '../lib/authFailure';
import { isExplicitlyOffline, ensureNetworkForAuthAction } from '../lib/offline/networkVerdict';
import { logNetError } from '../lib/diag/netError';
import { usePageTitle } from '../hooks/usePageTitle';
import { destinoDespuesDeEntrar } from '../lib/rutaPendiente';

// ── COS-34 — EL AVISO NO SE QUEDA DEBAJO DEL TECLADO ────────────────────────
//
// EL SÍNTOMA (iPhone): al fallar el intento —contraseña incorrecta, cualquier
// 4xx/5xx— el teclado se quedaba arriba y el aviso caía detrás de él. El usuario
// sólo veía que "no entra", sin saber POR QUÉ. El mensaje siempre estuvo; lo que
// faltaba era poder verlo.
//
// EL FIX ES SÓLO EL FOCO: se suelta el campo activo, iOS baja el teclado y el
// aviso queda a la vista. Ni la lógica de auth ni los mensajes se tocan.
//
// POR QUÉ A NIVEL DE MÓDULO: lo consumen `showLoginFailure` y `doLogin`, los dos
// memoizados. Una función recreada por render entraría en sus listas de
// dependencias y los invalidaría en cada render; aquí la identidad es estable y
// no toca ninguna dep.
//
// EL GUARD DE ETIQUETA: sólo se le quita el foco a un campo de texto. Si el
// usuario llegó al submit desde el botón (o desde el listener global de Enter
// con el foco en otro lado), no hay teclado que bajar y no se le roba el foco a
// nada. En web es inofensivo por el mismo motivo: no hay teclado que estorbe.
function bajarElTeclado() {
  const activo = typeof document !== 'undefined' ? document.activeElement : null;
  if (!activo || typeof activo.blur !== 'function') return;
  if (activo.tagName === 'INPUT' || activo.tagName === 'TEXTAREA') activo.blur();
}

export default function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  // BLOG-1-CIERRE (S6): la pestana en el idioma del usuario. El HTML servido trae el
  // titulo en ingles desde `scripts/prerender.js` (#442) y nadie lo volvia a tocar.
  usePageTitle(t('auth:login.meta_title'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLockoutMessage, setShowLockoutMessage] = useState(false);
  const { login, loginAsGuest, user } = useAuth();
  const navigate = useNavigate();
  // F6.7b (2ª pasada) — el DICTAMEN del vigía, no una sospecha. Con él el
  // formulario avisa al instante en vez de mandar una petición condenada a
  // agotar su techo de espera, y se DESTRABA SOLO en cuanto vuelve la señal:
  // el aviso y el botón cuelgan de este veredicto, no de un reloj propio.
  // En web el veredicto es siempre UNKNOWN (el vigía es app-only), así que
  // esta pantalla se comporta EXACTAMENTE como hoy allá.
  const net = useNetworkStatus();
  // F6.7b (pasada final 2) — EL SUBMIT QUE PELEA SOLO. El smoke midió una
  // ventana post-NET.UP (hasta ~2 min) donde el POST de login falla con
  // forma-de-red aunque el vigía ya dictaminó online: el transporte de iOS
  // todavía no sirve. Antes eso era un toast de error y un usuario reintentando
  // a mano; ahora es un estado "Conectando…" que reintenta solo (1s→2s→4s→cada
  // 5s) hasta entrar, hasta que el usuario cancele, o hasta que el fallo diga
  // algo de verdad (credenciales, servidor, dictamen offline).
  const { connecting, start: startConnecting, cancel: cancelConnecting } = useConnectingRetry();
  // AS-5 — los dos logins nativos (app-only). En web el hook se va en la primera línea de
  // su handler, así que ni siquiera pide el chunk del módulo nativo.
  // post-t7-1 (E1): quien entra llega a donde iba (la ruta que guardo el guardia) o al Panel
  const irAlPanel = useCallback(() => { navigate(destinoDespuesDeEntrar()); }, [navigate]);
  const { pendiente: pendienteSocial, entrarConApple, entrarConGoogle, soltar: soltarSocial } = useNativeSocialLogin({ onSuccess: irAlPanel });

  // TANDA 24 (bloque D, firma #226) — LOS DOS BOTONES DE LA WEB, CON ESTADO DE CARGA.
  //
  // POR QUÉ HACÍA FALTA, siendo que los dos handlers son SÍNCRONOS y sin red:
  // `redirectToGoogleLogin` y `redirectToAppleLogin` terminan en un
  // `window.location.href = ...`, y lo que el usuario percibe como "no pasa nada"
  // es la latencia de la navegación del navegador hacia `accounts.google.com` /
  // `appleid.apple.com`. En una conexión lenta son 1-3 segundos de página
  // congelada SIN una sola señal, con el botón exactamente igual que en reposo.
  //
  // ES UN ESTADO PROPIO Y NO EL DEL HOOK NATIVO: `pendienteSocial` lo gobierna
  // `useNativeSocialLogin`, que en web se va en la primera línea de su handler.
  // Los dos bloques son excluyentes (`IS_APP_BUILD`), así que nunca conviven.
  //
  // NO SE APAGA AL TOCAR: la página siguiente es la del proveedor.
  // CADUCA (estilos-1, Bloque E, apunte 1): "si el usuario vuelve con el botón de
  // atrás, el navegador re-monta este componente" no es cierto con la caché de ida
  // y vuelta (bfcache): la página regresa CON su estado y la ruedita se quedaba
  // pegada. Hoy el regreso (pageshow con persisted, popstate, o la pestaña o la app
  // que vuelve a estar visible) apaga las dos ruedas, la de la web y la nativa.
  const [pendienteWeb, setPendienteWeb] = useState(null);
  const irAProveedorWeb = useCallback((proveedor) => {
    // Primero el estado, luego el redirect: al revés, `window.location.href`
    // puede llevarse la página antes de que React pinte el spinner.
    setPendienteWeb(proveedor);
    if (proveedor === 'apple') redirectToAppleLogin();
    else redirectToGoogleLogin();
  }, []);
  useSoltarAlRegresar(useCallback(() => {
    setPendienteWeb(null);
    if (soltarSocial) soltarSocial();
  }, [soltarSocial]));

  // El dictamen offline corta el pleito: el aviso actual (banner + el portero
  // pidiendo una segunda opinión en cada tap) toma el relevo. Desde la T5-6 ya
  // no hay botones bloqueados: el dedo llega al portero y decide él.
  useEffect(() => {
    if (net.isOffline) cancelConnecting();
  }, [net.isOffline, cancelConnecting]);

  // Cambio de identidad → el pleito muere: jamás se reintenta con credenciales
  // que ya no son las que el usuario tiene en pantalla. (Cinturón: mientras
  // "Conectando…" está visible el formulario no se puede editar.)
  useEffect(() => { cancelConnecting(); }, [email, password, cancelConnecting]);

  // El error de un intento fallido, del más específico al más general. Los
  // detalles estructurados del backend (bloqueo por intentos, cuenta de Google)
  // siguen mandando; después los tres cubos que antes caían todos al mismo
  // mensaje mudo; y al final, los 4xx con un mensaje propio del servidor
  // (403 "falta verificar tu correo", 429 con su espera) que SÍ dicen algo
  // y no hay que pisar. Compartido por el primer intento y los reintentos.
  const showLoginFailure = useCallback((err) => {
    // COS-34 — el embudo por el que pasan TODOS los fallos del servidor (el
    // primer intento y cada peldaño del pleito): un solo punto y quedan
    // cubiertos los seis mensajes de abajo, no sólo el de credenciales.
    bajarElTeclado();
    const detail = err.response?.data?.detail;
    const kind = classifyAuthFailure(err);
    if (typeof detail === 'object' && detail?.show_reset_link) {
      setShowLockoutMessage(true);
    } else if (typeof detail === 'object' && detail?.code === 'google_account_no_password') {
      toast.error(t('login.toasts.google_account'));
    } else if (typeof detail === 'object' && detail?.code === 'apple_account_no_password') {
      toast.error(t('login.toasts.apple_account'));
    } else if (kind === AUTH_FAILURE.OFFLINE) {
      toast.error(t('login.toasts.no_connection'));
    } else if (kind === AUTH_FAILURE.SERVER) {
      toast.error(t('login.toasts.server_error'));
    } else if (kind === AUTH_FAILURE.CREDENTIALS) {
      // El único 401 con texto del backend es "Invalid email or password",
      // que además viaja SIEMPRE en inglés (no está en el mapa de api.js).
      toast.error(t('login.toasts.bad_credentials'));
    } else if (typeof detail === 'string') {
      toast.error(detail);
    } else if (detail && typeof detail.message === 'string') {
      toast.error(detail.message);
    } else {
      toast.error(t('login.toasts.login_failed_fallback'));
    }
  }, [t]);

  const doLogin = useCallback(async () => {
    if (loading || connecting) return;
    // COS-34 — los dos rechazos que NUNCA llegan a `showLoginFailure` (validación
    // de campos vacíos y el portero de red) también son fallos del submit, y su
    // aviso quedaba igual de tapado.
    if (!email || !password) { bajarElTeclado(); toast.error(t('login.toasts.fill_fields')); return; }
    // Sin señal DICTAMINADA: aviso ahora, no dentro de 10 s.
    //
    // F6.8 T5-5(b) — EL PORTERO PIDE UNA SEGUNDA OPINIÓN ANTES DE RECHAZAR.
    // Desde la T5 el DOWN puede venir de la semilla nativa del arranque, así que
    // antes de rechazar se le pregunta al sensor en ese instante (el racional
    // completo vive en ensureNetworkForAuthAction, en networkVerdict.js).
    // "Conectado" corrige el veredicto a UP y el submit sigue su curso normal;
    // cualquier otra cosa —offline confirmado, error, sin respuesta, flag
    // apagada, plugin ausente, web— rechaza con el toast de siempre.
    //
    // El `!== true` NO es paranoia de tipos: es el atajo síncrono del portero
    // (ver su docblock). Sin dictamen DOWN aquí no se espera nada.
    const gate = ensureNetworkForAuthAction(net.isOffline);
    if (gate !== true && !(await gate)) {
      bajarElTeclado();   // COS-34
      toast.error(t('login.toasts.no_connection'));
      return;
    }
    setLoading(true);
    setShowLockoutMessage(false);
    // El intento, compartido por el submit y por cada peldaño del pleito.
    // 'retry' SOLO con forma-de-red sin respuesta (red/DNS/timeout/cancelada) y
    // sin dictamen offline. Un 4xx de credenciales u otro error con significado
    // pinta su mensaje AHORA y termina — jamás se reintenta una contraseña mala.
    const attempt = async () => {
      try {
        await login(email, password);
        toast.success(t('login.toasts.success'));
        navigate(destinoDespuesDeEntrar());
        return 'done';
      } catch (err) {
        const kind = classifyAuthFailure(err);
        // Microscopio (pasada final 2): QUÉ forma tuvo el fallo, al visor.
        if (kind === AUTH_FAILURE.OFFLINE || kind === AUTH_FAILURE.SERVER) logNetError('login', err);
        if (kind === AUTH_FAILURE.OFFLINE && !isExplicitlyOffline()) return 'retry';
        showLoginFailure(err);
        return 'stop';
      }
    };
    try {
      const outcome = await attempt();
      if (outcome === 'retry') startConnecting(attempt);
    } finally {
      setLoading(false);
    }
  }, [email, password, loading, connecting, login, navigate, t, net.isOffline, showLoginFailure, startConnecting]);

  const handleSubmit = (e) => {
    e.preventDefault();
    doLogin();
  };

  // Global Enter key listener — triggers login from anywhere on the page
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        doLogin();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [doLogin]);

  const handleGuest = async () => {
    if (user) { navigate('/dashboard'); return; }
    // F6.8 T5-6 (C-16) — la MISMA segunda opinión que el submit. Entrar como
    // invitado también quedaba bloqueado por un DOWN mentiroso, y también es una
    // puerta, no una lectura degradable. (`!== true`: el atajo síncrono.)
    const gate = ensureNetworkForAuthAction(net.isOffline);
    if (gate !== true && !(await gate)) {
      toast.error(t('login.toasts.no_connection'));
      return;
    }
    setLoading(true);
    try {
      await loginAsGuest();
      toast.success(t('common:toasts.guest_welcome'));
      navigate('/dashboard');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 429) {
        toast.error(t('common:toasts.guest_rate_limit'));
      } else {
        toast.error(typeof detail === 'string' ? detail : t('common:toasts.guest_session_failed'));
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-4 bg-[#0e0e0e]">
      <FloatingLanguageSelector />
      <div className="w-full max-w-md space-y-4">
        <div className="text-center">
          <Link to="/" className="inline-block mb-3 font-['Space_Grotesk'] font-bold text-xl tracking-tight">
            Quiz<span className="text-[#FF6B00]">essa</span>
          </Link>
          <h1 className="text-2xl font-bold text-white" data-testid="login-title">{t('login.title')}</h1>
          <p className="text-[#6a6a6a] mt-1 text-sm">{t('login.subtitle')}</p>
        </div>

        <div className="glass-card rounded-[20px] p-6 space-y-4" data-testid="login-form">
          {/* F6.7b (pasada final 2) — el pleito visible: mientras la escalera
              reintenta, la tarjeta ENTERA es "Conectando…" + Cancelar. Nada del
              formulario queda tocable (ni el invitado, ni Google): un pleito a
              la vez, con una sola salida manual. Cancelar corta los reintentos
              y regresa al formulario tal cual estaba. */}
          {connecting ? (
            <div className="flex flex-col items-center gap-4 py-8" data-testid="login-connecting">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
              <p className="text-sm text-[#a0a0a0]" data-testid="login-connecting-msg">{t('connecting')}</p>
              <Button type="button" variant="outline" onClick={cancelConnecting}
                className="border-[#262626] text-[#a0a0a0] hover:text-white hover:bg-[#1f1f1f] rounded-xl h-11 px-6"
                data-testid="login-connect-cancel-btn">
                {t('common:cancel')}
              </Button>
            </div>
          ) : (<>
          {/* Aviso de red, colgado del veredicto del vigía: aparece en cuanto
              se pierde la señal y DESAPARECE SOLO en cuanto vuelve, sin
              reintentos, sin recargar y sin reloj propio. No es un toast
              porque no es un evento: es el estado del formulario. */}
          {net.isOffline && (
            <div className="bg-[#181818] border border-[#262626] rounded-xl p-3 text-sm text-[#a0a0a0]" data-testid="login-offline-notice">
              {t('login.toasts.no_connection_banner')}
            </div>
          )}
          {showLockoutMessage && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-400" data-testid="lockout-error">
              <Trans i18nKey="auth:login.toasts.lockout_template">
                Too many failed attempts. Please try again later, or <Link to="/forgot-password" className="text-[#FF6B00] hover:text-[#ff8c42] underline" data-testid="lockout-reset-link">reset your password</Link>.
              </Trans>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#a0a0a0] text-sm">{t('login.form.email_label')}</Label>
              <Input id="email" type="email" name="email" autoComplete="username" placeholder={t('login.form.email_placeholder')}
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-[#0e0e0e] border-[#262626] focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]/30 rounded-xl h-12"
                data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-[#a0a0a0] text-sm">{t('login.form.password_label')}</Label>
                <Link to="/forgot-password" tabIndex={-1} className="text-xs text-[#FF6B00] hover:text-[#ff8c42]" data-testid="forgot-password-link">
                  {t('login.forgot_link')}
                </Link>
              </div>
              <div className="relative">
                <Input id="password" type={showPw ? 'text' : 'password'} name="password" autoComplete="current-password" placeholder={t('login.form.password_placeholder')}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#0e0e0e] border-[#262626] focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]/30 rounded-xl h-12 pr-10"
                  data-testid="login-password-input" />
                <button type="button" tabIndex={-1} onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6a6a6a] hover:text-white"
                  data-testid="login-toggle-password">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {/* F6.8 T5-6 — EL BOTÓN YA NO SE ATA AL VEREDICTO. Hasta la T5 este
                `disabled` incluía `net.isOffline`, y con `disabled:pointer-events-none`
                eso dejaba la segunda opinión del portero alcanzable SOLO por la
                tecla Enter (C-12): con un DOWN mentiroso el usuario no podía ni
                intentar. Ahora el dedo llega al portero igual que el teclado, y
                es el portero —no el atributo— quien decide. El aviso visual de
                sin conexión es el banner de arriba, y SOLO ese: el `title` que
                repetía el mismo texto se fue en la T5c (C-21) porque colgaba un
                letrero de "no se puede" sobre un botón que sí se puede tocar, y
                sobre uno que además puede acabar entrando (el portero pide
                segunda opinión). */}
            <button type="submit" disabled={loading}
              className="inline-flex items-center justify-center w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl h-12 text-base font-semibold glow-primary disabled:opacity-50 disabled:pointer-events-none transition-colors"
              data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('login.form.submit_btn')}
            </button>
            <LegalAcceptance variant="continue" />
          </form>
          <p className="text-center text-sm text-[#6a6a6a]">
            {t('login.no_account_prompt')}{' '}
            <Link to="/signup" className="text-[#FF6B00] hover:text-[#ff8c42] font-medium" data-testid="login-signup-link">{t('login.no_account_link')}</Link>
          </p>
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#262626]" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#181818] px-3 text-[#6a6a6a]">{t('login.or_divider')}</span></div>
          </div>
          {/* Igual que el submit (T5-6): el invitado tampoco se ata al veredicto
              — su propio portero es el que decide.
              AS-5 (D-2): los tres botones de entrada se leen PAREJOS. El invitado venía
              de `text-[#6a6a6a]`, un escalón más apagado que Google y Apple sin ninguna
              razón.
              REMATE AS-5-TONO: el tono parejo de los tres subió otro escalón hacia el
              blanco, de `text-[#a0a0a0]` a `text-[#e5e5e5]` — el más claro que el repo ya
              usa como rótulo de control en reposo, con este mismo `hover:text-white`. */}
          <Button type="button" variant="outline" onClick={handleGuest} disabled={loading}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#1f1f1f] rounded-xl h-12"
            data-testid="login-guest-btn">
            <UserCircle className="w-4 h-4 mr-2" /> {t('login.guest_btn')}
          </Button>
          {/* AS-4 — Apple, WEB-ONLY: el flujo de redirect no corre dentro del WebView (el
              nativo va más abajo, gateado al revés).
              AS-5 (D-2): Apple va ARRIBA de Google en las tres superficies y en los dos
              mundos — es la entrada que Apple exige destacar en una app con logins de
              terceros, y la web se mantiene igual a la app para no tener dos memorias
              musculares distintas del mismo producto. */}
          {!IS_APP_BUILD && (
          <Button type="button" variant="outline" onClick={() => irAProveedorWeb('apple')}
            disabled={!!pendienteWeb}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#1f1f1f] rounded-xl h-12"
            data-testid="login-apple-btn">
            {pendienteWeb === 'apple'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>}
            {t('login.apple_btn')}
          </Button>
          )}
          {/* Google is WEB-ONLY: the OAuth redirect flow can't run inside the WebView.
              Native Google lives in the app-only block below. */}
          {!IS_APP_BUILD && (
          <Button type="button" variant="outline" onClick={() => irAProveedorWeb('google')}
            disabled={!!pendienteWeb}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#1f1f1f] rounded-xl h-12"
            data-testid="login-google-btn">
            {pendienteWeb === 'google'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>}
            {t('login.google_btn')}
          </Button>
          )}
          {/* AS-5 — los MISMOS dos proveedores, por la puerta NATIVA. Gate al revés que
              los de arriba: en la app la hoja del sistema sustituye al redirect. Mismos
              rótulos (cero texto nuevo), mismos logos, mismo tratamiento visual y el
              mismo h-12 (48px > los 44pt de tap target que pide Apple). Lo único que
              cambia es el handler — y que aquí sí hay estado de carga: una hoja nativa no
              se lleva la página por delante, así que sin esto el segundo toque abriría
              una segunda hoja. */}
          {IS_APP_BUILD && (
          <Button type="button" variant="outline" onClick={entrarConApple} disabled={!!pendienteSocial}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#1f1f1f] rounded-xl h-12"
            data-testid="login-apple-native-btn">
            {pendienteSocial === 'apple'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>}
            {t('login.apple_btn')}
          </Button>
          )}
          {IS_APP_BUILD && (
          <Button type="button" variant="outline" onClick={entrarConGoogle} disabled={!!pendienteSocial}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#1f1f1f] rounded-xl h-12"
            data-testid="login-google-native-btn">
            {pendienteSocial === 'google'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>}
            {t('login.google_btn')}
          </Button>
          )}
          </>)}
        </div>
      </div>
    </div>
  );
}
